/**
 * Maps the page the user is on to the user-guide section (and, for tabbed hubs, the heading) that
 * explains it. The anchors below must exist as `{#id}` headings in `layouts/docs/content/*`.
 */

/** Tab keys of each hub page; a matching heading id exists in the section's doc for every key. */
const TAB_ANCHORS: Record<string, readonly string[]> = {
  widgets: ["widgets", "queries"],
  anomalies: ["definitions", "incidents", "runs", "detectors"],
  "data-sources": ["adaptors", "ingest", "governance"],
  "data-prep": ["jobs", "approvals"],
  "ai-context": ["documents", "mapping", "examples"],
  logs: ["audit", "errors"],
  "super-admin": ["dashboard", "plans", "datasources", "mail", "workspaces", "users", "invites", "audit", "errors"],
};

type Rule = { test: RegExp; section: string; hash?: string };

/** First match wins, so edit screens come before their hub page. */
const RULES: Rule[] = [
  { test: /^\/widgets\/(new|[^/]+\/edit)$/, section: "widgets", hash: "widget-editor" },
  { test: /^\/dashboard-queries(\/|$)/, section: "widgets", hash: "queries" },
  { test: /^\/anomalies\/(new|[^/]+\/edit)$/, section: "anomalies", hash: "definition-fields" },
  { test: /^\/data-prep\/[^/]+\/design$/, section: "data-prep", hash: "designer" },
  { test: /^\/data-prep\/[^/]+\/runs$/, section: "data-prep", hash: "runs" },
  { test: /^\/home$/, section: "home" },
  { test: /^\/report$/, section: "report" },
  { test: /^\/dashboards(\/|$)/, section: "dashboards" },
  { test: /^\/widgets$/, section: "widgets" },
  { test: /^\/anomalies$/, section: "anomalies" },
  { test: /^\/insight-report-jobs$/, section: "scheduled-reports" },
  { test: /^\/data-sources$/, section: "data-sources" },
  { test: /^\/data-prep$/, section: "data-prep" },
  { test: /^\/schema-relations$/, section: "ai-context", hash: "schema" },
  { test: /^\/adaptor-discovery$/, section: "ai-context", hash: "adaptor" },
  { test: /^\/semantic-models$/, section: "warehouse", hash: "semantic-models" },
  { test: /^\/warehouse-usage$/, section: "warehouse", hash: "usage" },
  { test: /^\/openapi-catalogs$/, section: "ai-context", hash: "openapi" },
  { test: /^\/ai-context$/, section: "ai-context" },
  { test: /^\/logs$/, section: "logs" },
  { test: /^\/setup$/, section: "workspace-setup" },
  { test: /^\/workspace-settings$/, section: "workspace-settings" },
  { test: /^\/super-admin(?:\/([^/]+))?$/, section: "super-admin" },
];

export const DEFAULT_HELP_PATH = "/docs/getting-started";

/** True on the documentation pages themselves, where a help button would point at the current page. */
export function isDocsPath(pathname: string): boolean {
  return pathname === "/docs" || pathname.startsWith("/docs/") || pathname === "/release-notes";
}

/** Docs address that explains `pathname` (+ the `?tab=` of a hub page). */
export function resolveHelpTarget(pathname: string, search = ""): string {
  const normalized = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
  const rule = RULES.find((r) => r.test.test(normalized));
  if (!rule) {
    return DEFAULT_HELP_PATH;
  }
  let hash = rule.hash;
  if (!hash) {
    const pathSection = normalized.match(/^\/super-admin\/([^/]+)/)?.[1];
    const tab = pathSection || new URLSearchParams(search).get("tab");
    if (tab && TAB_ANCHORS[rule.section]?.includes(tab)) {
      hash = tab;
    }
  }
  return `/docs/${rule.section}${hash ? `#${hash}` : ""}`;
}
